import type { BookingPartner, TravelpayoutsAccount } from '@/_constants/BookingPartner.ts';
import type { Party, StayDates } from '@/utils/booking/bookingTrip.ts';

export type HotelTarget = {
  kind: 'hotel';
  place: string;
  city: string | null;
  agodaHotelId: number | null;
  agodaCityId: number | null;
  dates: StayDates | null;
  party: Party;
};

export type ActivityTarget = { kind: 'activity'; searchText: string; name: string };

export type LinkTarget = HotelTarget | ActivityTarget;

const hotelSearchText = (hotel: HotelTarget): string =>
  [hotel.place, hotel.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

const setDates = (url: URL, dates: StayDates | null, checkInKey: string, checkOutKey: string) => {
  if (!dates) return;
  url.searchParams.set(checkInKey, dates.checkIn);
  url.searchParams.set(checkOutKey, dates.checkOut);
};

const cjClickUrl = (websiteId: string, linkId: string, target: string, tripId: string): string => {
  const url = new URL(`https://www.anrdoezrs.net/click-${websiteId}-${linkId}`);
  url.searchParams.set('sid', tripId);
  url.searchParams.set('url', target);
  return url.toString();
};

const bookingComSearch = (hotel: HotelTarget): string => {
  const url = new URL('https://www.booking.com/searchresults.en-gb.html');
  url.searchParams.set('ss', hotelSearchText(hotel));
  setDates(url, hotel.dates, 'checkin', 'checkout');
  url.searchParams.set('group_adults', String(hotel.party.adults));
  url.searchParams.set('group_children', String(hotel.party.childAges.length));
  hotel.party.childAges.forEach((age) => url.searchParams.append('age', String(age)));
  url.searchParams.set('no_rooms', '1');
  return url.toString();
};

const expediaSearch = (hotel: HotelTarget): string => {
  const url = new URL('https://www.expedia.co.uk/Hotel-Search');
  url.searchParams.set('destination', hotelSearchText(hotel));
  setDates(url, hotel.dates, 'startDate', 'endDate');
  url.searchParams.set('adults', String(hotel.party.adults));
  return url.toString();
};

const agodaUrl = (cid: string, hotel: HotelTarget, tripId: string): string | null => {
  if (hotel.agodaHotelId === null && hotel.agodaCityId === null) return null;

  const url = new URL('https://www.agoda.com/partners/partnersearch.aspx');
  url.searchParams.set('pcs', '1');
  url.searchParams.set('cid', cid);

  if (hotel.agodaHotelId !== null) {
    url.searchParams.set('hid', String(hotel.agodaHotelId));
  } else {
    url.searchParams.set('city', String(hotel.agodaCityId));
  }

  setDates(url, hotel.dates, 'checkin', 'checkout');
  url.searchParams.set('adults', String(hotel.party.adults));
  url.searchParams.set('rooms', '1');
  url.searchParams.set('childs', String(hotel.party.childAges.length));

  if (hotel.party.childAges.length > 0) {
    url.searchParams.set('childages', hotel.party.childAges.join(','));
  }

  url.searchParams.set('tag', tripId);
  return url.toString();
};

const getYourGuideUrl = (partnerId: string, searchText: string, tripId: string): string => {
  const url = new URL('https://www.getyourguide.com/s/');
  url.searchParams.set('q', searchText);
  url.searchParams.set('partner_id', partnerId);
  url.searchParams.set('utm_medium', 'travel_agent');
  url.searchParams.set('travel_agent', '1');
  url.searchParams.set('cmp', tripId);
  return url.toString();
};

const viatorUrl = (pid: string, mcid: string, searchText: string, tripId: string): string => {
  const url = new URL('https://www.viator.com/searchResults/all');
  url.searchParams.set('text', searchText);
  url.searchParams.set('pid', pid);
  url.searchParams.set('mcid', mcid);
  url.searchParams.set('medium', 'link');
  url.searchParams.set('campaign', tripId);
  return url.toString();
};

const travelpayoutsUrl = (
  account: Readonly<TravelpayoutsAccount>,
  searchText: string,
  tripId: string
): string | null => {
  if (!account.programme || !account.campaignId) return null;

  const target = new URL(account.searchUrl);
  target.searchParams.set(account.searchParam, searchText);

  const url = new URL('https://tp.media/r');
  url.searchParams.set('marker', account.marker);
  url.searchParams.set('trs', account.trs);
  url.searchParams.set('p', account.programme);
  url.searchParams.set('campaign_id', account.campaignId);
  url.searchParams.set('sub_id', tripId);
  url.searchParams.set('u', target.toString());
  return url.toString();
};

const partnerLink = (partner: BookingPartner, target: LinkTarget, tripId: string): string | null => {
  if (partner.covers !== target.kind) return null;

  if (target.kind === 'hotel') {
    if (!hotelSearchText(target)) return null;

    switch (partner.key) {
      case 'booking_com':
        return cjClickUrl(partner.account.cjWebsiteId, partner.account.cjLinkId, bookingComSearch(target), tripId);
      case 'agoda':
        return agodaUrl(partner.account.cid, target, tripId);
      case 'expedia':
        return partner.account.cjLinkId
          ? cjClickUrl(partner.account.cjWebsiteId, partner.account.cjLinkId, expediaSearch(target), tripId)
          : null;
      default:
        return null;
    }
  }

  const searchText = target.searchText.trim();

  if (!searchText) return null;

  switch (partner.key) {
    case 'getyourguide':
      return getYourGuideUrl(partner.account.partnerId, searchText, tripId);
    case 'viator':
      return viatorUrl(partner.account.pid, partner.account.mcid, searchText, tripId);
    case 'tiqets':
      return travelpayoutsUrl(partner.account, target.name.trim() || searchText, tripId);
    case 'klook':
    case 'kkday':
      return travelpayoutsUrl(partner.account, searchText, tripId);
    default:
      return null;
  }
};

export default partnerLink;
